import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Switch from '@mui/material/Switch';
import { staffService, roleService, machineService, workShiftService } from '../services/api';
import { PERMISSION_MATRIX } from '../utils/permissionMatrix';
import { fmtMachineLabel, sortMachinesByIdentifier } from '../utils/machineLabel';

// ─── Shifts section ───────────────────────────────────────────────────────────
function ShiftsSection() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await workShiftService.getAll();
      setShifts(r.data?.results || r.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setName(''); setIsActive(true); setStartTime(''); setEndTime(''); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setName(s.name); setIsActive(s.is_active); setStartTime(s.start_time || ''); setEndTime(s.end_time || ''); setOpen(true); };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), is_active: isActive, start_time: startTime || null, end_time: endTime || null };
      if (editing) {
        await workShiftService.patch(editing.id, payload);
      } else {
        await workShiftService.create(payload);
      }
      setOpen(false);
      load();
    } catch (e) {
      alert(e.response?.data?.name?.[0] || e.response?.data?.detail || 'Помилка збереження');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити зміну?')) return;
    try {
      await workShiftService.delete(id);
      load();
    } catch { alert('Помилка видалення'); }
  };

  if (loading) return <CircularProgress sx={{ m: 3 }} />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Робочі зміни</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Додати зміну</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Початок</TableCell>
              <TableCell>Кінець</TableCell>
              <TableCell>Активна</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.start_time || '—'}</TableCell>
                <TableCell>{s.end_time || '—'}</TableCell>
                <TableCell>
                  <Chip label={s.is_active ? 'Так' : 'Ні'} size="small"
                    color={s.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(s)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(s.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {shifts.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center">Зміни не створені</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Редагувати зміну' : 'Нова зміна'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Назва зміни" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Початок зміни"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Кінець зміни"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Якщо менше за початок — зміна переходить через північ"
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label="Активна" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const PERM_INHERIT = 'inherit';
const PERM_ALLOW = 'allow';
const PERM_DENY = 'deny';

function buildInitialPermissions(permissionsFromApi) {
  const raw = permissionsFromApi && typeof permissionsFromApi === 'object' ? permissionsFromApi : {};
  const result = {};
  PERMISSION_MATRIX.forEach(({ permissions: perms }) => {
    perms.forEach(({ key }) => {
      if (raw[key] === true) result[key] = PERM_ALLOW;
      else if (raw[key] === false) result[key] = PERM_DENY;
      else result[key] = PERM_INHERIT;
    });
  });
  return result;
}

function StaffPage() {
  const [tab, setTab] = useState(0);
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    password: '',
    role: '',
    shifts: [],
    is_active: true,
    machines: [],
    permissions: {},
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [staffRes, rolesRes, machinesRes, shiftsRes] = await Promise.all([
        staffService.getAll(),
        roleService.getAll(),
        machineService.getAllForSelect ? machineService.getAllForSelect() : machineService.getAll(),
        workShiftService.getAll(),
      ]);
      setStaff(staffRes.data.results || staffRes.data);
      setRoles(rolesRes.data.results || rolesRes.data);
      setMachines(sortMachinesByIdentifier(machinesRes.data.results || machinesRes.data || []));
      setShifts(shiftsRes.data?.results || shiftsRes.data || []);
    } catch (err) {
      console.error('Помилка завантаження:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (item = null) => {
    setError('');
    if (item) {
      setEditing(item.id);
      setFormData({
        full_name: item.full_name || '',
        username: item.username || '',
        password: '',
        role: item.role || item.role_id || '',
        shifts: (item.shifts || []).map(Number),
        is_active: item.is_active !== false,
        machines: (item.machines_data || item.machines || []).map((m) => (m && typeof m === 'object' ? m.id : m)),
        permissions: buildInitialPermissions(item.permissions),
      });
    } else {
      setEditing(null);
      setFormData({
        full_name: '',
        username: '',
        password: '',
        role: '',
        shifts: [],
        is_active: true,
        machines: [],
        permissions: buildInitialPermissions({}),
      });
    }
    setOpen(true);
  };

  const setPermission = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: value },
    }));
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.full_name.trim()) {
      setError('ПІБ обовʼязковий');
      return;
    }
    if (!formData.username.trim()) {
      setError('Логін обовʼязковий');
      return;
    }
    if (!editing && !formData.password) {
      setError('Пароль обовʼязковий для нового співробітника');
      return;
    }

    const permissionsPayload = {};
    Object.entries(formData.permissions || {}).forEach(([key, value]) => {
      if (value === PERM_ALLOW) permissionsPayload[key] = true;
      else if (value === PERM_DENY) permissionsPayload[key] = false;
    });

    setSaving(true);
    try {
      const payload = {
        full_name: formData.full_name.trim(),
        username: formData.username.trim(),
        role: formData.role || null,
        shifts: formData.shifts,
        is_active: formData.is_active,
        machines: formData.machines,
        permissions: permissionsPayload,
      };
      if (formData.password) payload.password = formData.password;

      if (editing) {
        await staffService.update(editing, payload);
      } else {
        await staffService.create(payload);
      }
      await loadData();
      handleClose();
    } catch (err) {
      const msg = err.response?.data?.username?.[0]
        || err.response?.data?.full_name?.[0]
        || err.response?.data?.detail
        || 'Помилка збереження';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити цього співробітника?')) return;
    try {
      await staffService.delete(id);
      await loadData();
    } catch (err) {
      console.error('Помилка видалення:', err);
      alert(err.response?.data?.detail || 'Не вдалося видалити');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" mb={2}>Персонал та зміни</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Співробітники" />
        <Tab label="Зміни" />
      </Tabs>

      {tab === 1 && <ShiftsSection />}

      {tab === 0 && <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Додати співробітника
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>ПІБ</TableCell>
              <TableCell>Логін</TableCell>
              <TableCell>Роль</TableCell>
              <TableCell>Зміна</TableCell>
              <TableCell>Активний</TableCell>
              <TableCell>Станки</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">Немає записів</Typography>
                </TableCell>
              </TableRow>
            ) : (
              staff.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.full_name}</TableCell>
                  <TableCell>{item.username}</TableCell>
                  <TableCell>{item.role_name || '—'}</TableCell>
                  <TableCell>{(item.shift_names || []).join(', ') || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.is_active !== false ? 'Так' : 'Ні'}
                      color={item.is_active !== false ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {(item.machines_data || item.machines || [])
                      .map((m) => (m && typeof m === 'object' ? fmtMachineLabel(m) : (machines.find((x) => x.id === m)?.identifier || m)))
                      .join(', ') || '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpen(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати співробітника' : 'Новий співробітник'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Typography color="error">{error}</Typography>}
            <TextField
              label="ПІБ"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Логін"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              fullWidth
              required
              disabled={!!editing}
            />
            <TextField
              label={editing ? 'Новий пароль (залиште порожнім)' : 'Пароль'}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              required={!editing}
            />
            <FormControl fullWidth>
              <InputLabel id="role-label">Роль</InputLabel>
              <Select
                labelId="role-label"
                value={formData.role}
                label="Роль"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="">— без ролі —</MenuItem>
                {roles.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="shifts-label">Зміни</InputLabel>
              <Select
                labelId="shifts-label"
                multiple
                value={formData.shifts}
                label="Зміни"
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.includes('__all__')) {
                    const allIds = shifts.map(s => s.id);
                    setFormData(prev => ({ ...prev, shifts: prev.shifts.length === allIds.length ? [] : allIds }));
                  } else {
                    setFormData(prev => ({ ...prev, shifts: val }));
                  }
                }}
                renderValue={(selected) => selected.map((id) => shifts.find((s) => s.id === id)?.name || id).join(', ')}
              >
                <MenuItem value="__all__">
                  <Checkbox
                    checked={formData.shifts.length === shifts.length && shifts.length > 0}
                    indeterminate={formData.shifts.length > 0 && formData.shifts.length < shifts.length}
                  />
                  <em>Обрати всі</em>
                </MenuItem>
                {shifts.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox checked={formData.shifts.includes(s.id)} />
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Активний"
            />
            <FormControl fullWidth>
              <InputLabel id="machines-label">Станки оператора</InputLabel>
              <Select
                labelId="machines-label"
                multiple
                value={formData.machines}
                label="Станки оператора"
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.includes('__all__')) {
                    const allIds = machines.map(m => m.id);
                    setFormData(prev => ({ ...prev, machines: prev.machines.length === allIds.length ? [] : allIds }));
                  } else {
                    setFormData(prev => ({ ...prev, machines: val }));
                  }
                }}
                renderValue={(selected) =>
                  selected
                    .map((id) => {
                      const m = machines.find((x) => x.id === id);
                      return m ? fmtMachineLabel(m) : id;
                    })
                    .join(', ')
                }
              >
                <MenuItem value="__all__">
                  <Checkbox
                    checked={formData.machines.length === machines.length && machines.length > 0}
                    indeterminate={formData.machines.length > 0 && formData.machines.length < machines.length}
                  />
                  <em>Обрати всі</em>
                </MenuItem>
                {machines.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    <Checkbox checked={formData.machines.includes(m.id)} />
                    {fmtMachineLabel(m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              Персональні права (перевизначають роль)
            </Typography>
            {PERMISSION_MATRIX.map(({ section, permissions: perms }) => (
              <Box key={section} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {section}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {perms.map(({ key, label }) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ minWidth: 200 }} variant="body2">
                        {label}
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                          value={formData.permissions[key] ?? PERM_INHERIT}
                          onChange={(e) => setPermission(key, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value={PERM_INHERIT}>Успадкувати з ролі</MenuItem>
                          <MenuItem value={PERM_ALLOW}>Дозволити</MenuItem>
                          <MenuItem value={PERM_DENY}>Заборонити</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
      </>}
    </Box>
  );
}

export default StaffPage;
